import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import type { FraxlendPairRaw, FraxlendPairsRaw } from './types'

/**
 * Synthesized per-pair lender key, e.g. `FRAXLEND_1_AB3CB84C…`. The chain id
 * rides in the key (the Fluid / River / Resupply / Curvance convention) even
 * though Fraxlend is Ethereum-only today.
 */
export function fraxlendLenderKey(
  lender: string,
  chainId: string | number,
  pair: string,
): string {
  return `${lender}_${chainId}_${pair.replace(/^0x/i, '').toUpperCase()}`
}

/** Recover `{ lender, chainId, pair }` from a per-pair key. */
export function fraxlendKeyParts(
  key: string,
): { lender: string; chainId: string; pair: string } | undefined {
  if (!key.startsWith('FRAXLEND_')) return undefined
  const m = key.slice('FRAXLEND_'.length).match(/^(\d+)_([0-9A-Fa-f]{40})$/)
  if (!m) return undefined
  return { lender: 'FRAXLEND', chainId: m[1], pair: `0x${m[2].toLowerCase()}` }
}

const SECONDS_PER_YEAR = 31_536_000

/** 1e18-scaled per-second rate -> nominal APR in percent (package convention). */
const ratePerSecToApr = (rate: bigint): number =>
  (Number(rate) / 1e18) * SECONDS_PER_YEAR * 100

const toFloat = (raw: bigint, decimals: number) => Number(raw) / 10 ** decimals

const MAX_UINT = (1n << 256n) - 1n

function currencyFor(
  address: string,
  decimals: number,
  symbol: string,
  name: string,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol, name, decimals }
}

/**
 * How many ASSET units one COLLATERAL unit is worth, per the pair's own oracle.
 *
 * **`exchangeRate` is COLLATERAL-PER-ASSET — it is INVERTED relative to every
 * other lender we carry**, and it is NOT decimal-normalised. Fraxlend's own LTV
 * math is
 *   `ltv = borrowAmount * exchangeRate * LTV_PRECISION
 *          / (collateralAmount * EXCHANGE_PRECISION)`
 * which is only dimensionless if `exchangeRate` carries units of
 * collateral-per-asset in RAW BASE UNITS. So inverting it needs the decimal
 * correction too:
 *
 *   assetPerCollateral = (EXCHANGE_PRECISION / exchangeRate)
 *                        * 10^(collateralDecimals - assetDecimals)
 *
 * Verified on-chain 2026-08-11 across three decimal shapes: frxUSD/sfrxETH
 * (18/18) -> 2162.2, frxUSD/WBTC (18/**8**) -> 60,386.5, frxUSD/sfrxUSD
 * (18/18) -> 1.2009. The WBTC pair is the one that catches a missing decimal
 * term — without it the price comes out 1e10 too large. The sfrxUSD figure was
 * independently confirmed against a simulated swap through the pair's approved
 * leverage swapper (1e18 frxUSD -> 0.8327e18 sfrxUSD = 1/1.2009).
 *
 * `which`: the protocol is deliberately two-sided. Use the HIGH rate to value
 * collateral for a BORROW (conservative: collateral looks cheaper) and the LOW
 * rate for a liquidation check. They are equal on pairs whose oracle is a
 * deterministic ERC-4626 share price.
 */
export function fraxlendAssetPerCollateral(
  p: FraxlendPairRaw,
  which: 'low' | 'high' = 'high',
): number {
  const rate = which === 'high' ? p.highExchangeRate : p.lowExchangeRate
  if (rate === 0n) return 0
  const base = Number(p.exchangePrecision) / Number(rate)
  return base * 10 ** (p.collateralDecimals - p.assetDecimals)
}

/**
 * Map one Fraxlend deployment's on-chain batch into the shared
 * `MorphoGeneralPublicResponse` shape, keyed `FRAXLEND_<chainId>_<PAIR>`.
 *
 * Modelling decisions worth knowing:
 *
 *  - **A pair publishes TWO rows and they are ASYMMETRIC.** The `asset` leg is
 *    supply + borrow (the pair itself is that leg's ERC-4626 vault). The
 *    `collateral` leg is deposit-only: no lender side, no borrow side, 0 %
 *    supply rate. That is not a gap in the data — Fraxlend genuinely pays
 *    nothing on posted collateral, and its return is the underlying's own
 *    intrinsic yield, which the yields layer attaches separately. Publishing a
 *    supply rate there would double-count.
 *  - **`maxLTV` is scaled by `LTV_PRECISION = 1e5`**, not WAD and not bps.
 *  - **The LTV belongs to the COLLATERAL row.** Fraxlend has exactly one
 *    collateral and one debt, so the pair-level `maxLTV` IS that row's
 *    borrow-collateral factor; the asset row is never collateral.
 *  - **Liquidation threshold == maxLTV.** Fraxlend has a single ratio: the
 *    same `maxLTV` gates both opening a borrow and being liquidated
 *    (`_isSolvent` uses it verbatim). There is no separate LT, so publishing
 *    one would invent a safety buffer that does not exist.
 *  - **Prices are derived from the pair's own oracle where possible.** The
 *    shared price map covers the asset leg (frxUSD / FRAX / crvUSD / DOLA are
 *    all well-priced), and the collateral leg is then priced RELATIVELY via
 *    `fraxlendAssetPerCollateral`. That is strictly better than looking the
 *    collateral up independently: it is the same number the protocol enforces
 *    limits with, so health factors we publish agree with the chain. We fall
 *    back to the price map only if the oracle read is unusable.
 *  - **Caps are `type(uint256).max` on every live pair.** A pair is frozen by
 *    setting them to 0, since v3.1.0 has NO deposit/borrow pause flag — the
 *    four flags it does have cover repay / withdraw / liquidate / interest.
 *  - **`isFrozen` means "cannot be entered", not "dead".** Exits stay open by
 *    design so users can close; the Lista lesson is that a market in run-off
 *    must keep its UI.
 */
export function convertFraxlendPairsToResponse(
  raw: FraxlendPairsRaw,
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}

  for (const p of raw?.pairs ?? []) {
    const lenderKey = fraxlendLenderKey(raw.lender, chainId, p.pair)

    // @ts-ignore — built incrementally, matches the Curvance/Resupply converters
    const entry: MorphoGeneralPublicResponse = { data: {} }

    const assetToken = tokens[p.asset]
    const collToken = tokens[p.collateral]
    const assetPriceKey =
      toOracleKey(assetToken?.assetGroup!) || toGenericPriceKey(p.asset, chainId)
    const collPriceKey =
      toOracleKey(collToken?.assetGroup!) ||
      toGenericPriceKey(p.collateral, chainId)

    const assetPrice = prices[assetPriceKey] || 0
    // Price the collateral through the PAIR'S OWN oracle so our health factor
    // agrees with `_isSolvent`. Only fall back to the shared map if that is
    // unusable (a zeroed / never-initialised exchange rate).
    const assetPerColl = fraxlendAssetPerCollateral(p, 'high')
    const collPrice =
      assetPerColl > 0 && assetPrice > 0
        ? assetPerColl * assetPrice
        : prices[collPriceKey] || 0

    // --- accounting ---
    const totalDeposits = toFloat(p.totalAssetAmount, p.assetDecimals)
    const totalDebt = toFloat(p.totalBorrowAmount, p.assetDecimals)
    // Idle asset = what a borrow or a withdrawal can actually draw on.
    const liquidity = Math.max(totalDeposits - totalDebt, 0)
    const totalCollateral = toFloat(p.totalCollateral, p.collateralDecimals)
    const utilization = totalDeposits > 0 ? totalDebt / totalDeposits : 0

    const borrowApr = ratePerSecToApr(p.ratePerSec)
    // Supplier APR: borrower interest, less the protocol cut, spread over the
    // whole supplied base (not just the borrowed part) — the standard identity.
    // `feeToProtocolRate` is scaled by LTV_PRECISION-style 1e5.
    const protocolCut = Math.min(Math.max(p.feeToProtocolRate / 1e5, 0), 1)
    const supplyApr = borrowApr * utilization * (1 - protocolCut)

    const ltv =
      p.ltvPrecision > 0n ? Number(p.maxLtv) / Number(p.ltvPrecision) : 0
    // Fraxlend's liquidation fee is the liquidator's bonus, scaled by
    // LIQ_PRECISION. `cleanLiquidationFee` is 0 in the batch (not read) — the
    // descriptor carries what we do have and consumers should treat a 0 as
    // "unknown", not "free".
    const liquidationPenalty =
      p.liqPrecision > 0n && p.cleanLiquidationFee > 0n
        ? Number(p.cleanLiquidationFee) / Number(p.liqPrecision)
        : 0

    // Caps: max-uint means uncapped. 0 is the freeze (there is no pause flag).
    const uncappedDeposit = p.depositLimit >= MAX_UINT / 2n
    const uncappedBorrow = p.borrowLimit >= MAX_UINT / 2n
    const depositHeadroom = uncappedDeposit
      ? Number.POSITIVE_INFINITY
      : Math.max(toFloat(p.depositLimit, p.assetDecimals) - totalDeposits, 0)
    const borrowHeadroom = uncappedBorrow
      ? Number.POSITIVE_INFINITY
      : Math.max(toFloat(p.borrowLimit, p.assetDecimals) - totalDebt, 0)

    const depositsHalted = depositHeadroom <= 0
    const borrowHalted = borrowHeadroom <= 0
    const borrowLiquidity = borrowHalted
      ? 0
      : Math.min(liquidity, borrowHeadroom)

    // ---------------- asset leg: supply + borrow, never collateral ----------
    const assetUid = createMarketUid(chainId, lenderKey, p.asset)
    entry.data[assetUid] = {
      marketUid: assetUid,
      name: p.assetSymbol,
      poolId: p.pair.toLowerCase(),
      underlying: p.asset,
      asset: currencyFor(
        p.asset,
        p.assetDecimals,
        p.assetSymbol,
        p.assetName,
        tokens,
      ),
      totalDeposits,
      totalDebtStable: 0,
      totalDebt,
      totalLiquidity: liquidity,
      borrowLiquidity,
      totalLiquidityUSD: liquidity * assetPrice,
      borrowLiquidityUSD: borrowLiquidity * assetPrice,
      totalDepositsUSD: totalDeposits * assetPrice,
      totalDebtStableUSD: 0,
      totalDebtUSD: totalDebt * assetPrice,
      utilization,
      depositRate: supplyApr,
      variableBorrowRate: borrowApr,
      stableBorrowRate: 0,
      rateModel: 'fraxlend-variable-v3',
      intrinsicYield: 0,
      rewards: undefined,
      decimals: p.assetDecimals,
      config: {
        0: {
          category: 0,
          // The asset can never be posted as collateral in its own pair.
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty,
          closeFactor: 1,
          collateralDisabled: true,
          debtDisabled: borrowHalted,
        },
      },
      closeFactor: 1,
      collateralActive: false,
      borrowingEnabled: !borrowHalted,
      depositsEnabled: !depositsHalted,
      hasStable: false,
      variableBorrowDisabled: borrowHalted,
      isActive: !(depositsHalted && borrowHalted),
      isFrozen: depositsHalted && borrowHalted,
    } as any

    // ---------------- collateral leg: deposit-only ------------------------
    const collUid = createMarketUid(chainId, lenderKey, p.collateral)
    entry.data[collUid] = {
      marketUid: collUid,
      name: p.collateralSymbol,
      poolId: p.pair.toLowerCase(),
      underlying: p.collateral,
      asset: currencyFor(
        p.collateral,
        p.collateralDecimals,
        p.collateralSymbol,
        p.collateralName,
        tokens,
      ),
      totalDeposits: totalCollateral,
      totalDebtStable: 0,
      totalDebt: 0,
      // Posted collateral is not lendable liquidity — it just sits there.
      totalLiquidity: 0,
      borrowLiquidity: 0,
      totalLiquidityUSD: 0,
      borrowLiquidityUSD: 0,
      totalDepositsUSD: totalCollateral * collPrice,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      utilization: 0,
      // Genuinely 0 %: there is no lender side on the collateral leg.
      depositRate: 0,
      variableBorrowRate: 0,
      stableBorrowRate: 0,
      rateModel: 'fraxlend-variable-v3',
      intrinsicYield: 0,
      rewards: undefined,
      decimals: p.collateralDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: ltv,
          // Fraxlend has ONE ratio: `maxLTV` gates both opening and solvency,
          // so the liquidation threshold IS the LTV. Publishing anything
          // higher would invent a buffer the contract does not give.
          collateralFactor: ltv,
          borrowFactor: 1,
          liquidationPenalty,
          closeFactor: 1,
          collateralDisabled: false,
          debtDisabled: true,
        },
      },
      closeFactor: 1,
      collateralActive: true,
      borrowingEnabled: false,
      depositsEnabled: !depositsHalted,
      hasStable: false,
      variableBorrowDisabled: true,
      isActive: true,
      isFrozen: depositsHalted && borrowHalted,
    } as any
    // `params` is the CANONICAL per-market carrier — 12 of 13 converters set
    // it and real consumers read it (the term-sheet route reads
    // `params.market`, the loop layer reads `params.collateralRate`). The
    // richer `descriptor` below is Fraxlend-specific detail; anything a
    // GENERIC caller needs must be here, or it is invisible.
    //
    // `leverageEnabled` in particular: without it a UI cannot know that a pair
    // has no approved swapper, so it offers a loop button that 400s. That is
    // exactly what happens on the flagship frxUSD/sfrxETH pair, which holds
    // ~68 % of all Fraxlend borrows and cannot loop.
    ;(entry as any).params = {
      market: {
        lender: lenderKey,
        name: `${p.assetSymbol} / ${p.collateralSymbol}`,
        id: p.pair.toLowerCase(),
        loanAddress: p.asset,
        collateralAddress: p.collateral,
        loanDecimals: p.assetDecimals,
        collateralDecimals: p.collateralDecimals,
        /** RAW, on the pair's own 1e5 scale — and it is ALSO the liquidation
         *  threshold, so there is no separate LT to publish. */
        lltv: p.maxLtv.toString(),
        oracle: p.oracle,
        irm: p.rateContract,
        /** Whether the NATIVE loop is available on this pair right now,
         *  probed live. Gate any leverage UI on this. */
        leverageEnabled: p.leverageSwapperApproved,
      },
    }

    ;(entry as any).descriptor = {
      pair: p.pair.toLowerCase(),
      symbol: p.symbol,
      fTokenDecimals: p.decimals,
      asset: p.asset,
      collateral: p.collateral,
      assetDecimals: p.assetDecimals,
      collateralDecimals: p.collateralDecimals,
      /** RAW, with its scale, so a caller can reproduce the protocol's math. */
      maxLtvRaw: p.maxLtv.toString(),
      ltvPrecision: p.ltvPrecision.toString(),
      maxLtv: ltv,
      /** INVERTED (collateral-per-asset) and NOT decimal-normalised — see
       *  `fraxlendAssetPerCollateral` before using these directly. */
      lowExchangeRate: p.lowExchangeRate.toString(),
      highExchangeRate: p.highExchangeRate.toString(),
      exchangePrecision: p.exchangePrecision.toString(),
      /** The inverted, decimal-corrected, ready-to-use forms. */
      assetPerCollateralHigh: fraxlendAssetPerCollateral(p, 'high'),
      assetPerCollateralLow: fraxlendAssetPerCollateral(p, 'low'),
      oracle: p.oracle,
      maxOracleDeviation: p.maxOracleDeviation,
      /** Can be DAYS stale on this protocol — surface it, do not hide it. */
      exchangeRateLastTimestamp: p.exchangeRateLastTimestamp.toString(),
      /** Borrow SHARES outstanding; convert with `toBorrowAmount`. */
      totalBorrowShares: p.totalBorrowShares.toString(),
      totalAssetShares: p.totalAssetShares.toString(),
      ratePerSecond: p.ratePerSec.toString(),
      /** The STATEFUL IRM term — needed to reproduce the rate offline. */
      fullUtilizationRate: p.fullUtilizationRate.toString(),
      feeToProtocolRate: p.feeToProtocolRate,
      /** `VariableInterestRateV3`; `getNewRate` is a view ⇒ exact
       *  rate-at-depth with no modelling. */
      rateContract: p.rateContract,
      depositLimit: p.depositLimit.toString(),
      borrowLimit: p.borrowLimit.toString(),
      isRepayPaused: p.isRepayPaused,
      isWithdrawPaused: p.isWithdrawPaused,
      isLiquidatePaused: p.isLiquidatePaused,
      isInterestPaused: p.isInterestPaused,
      /**
       * Native looping availability, PROBED LIVE this refresh. `false` means
       * `leveragedPosition` reverts `BadSwapper()` on this pair — it does NOT
       * mean the protocol lacks the feature. Never cache across refreshes.
       */
      leverageEnabled: p.leverageSwapperApproved,
      leverageSwapper: p.leverageSwapper,
      /** `path` for `leveragedPosition` / `repayAssetWithCollateral`. The
       *  loop direction is asset->collateral; deleverage is the reverse. */
      leveragePath: [p.asset, p.collateral],
      deleveragePath: [p.collateral, p.asset],
    }

    out[lenderKey] = entry
  }

  return out
}
